import {
  EmailValidator,
  Form,
  FormField,
  IFormField,
  RequiredValidator,
} from '@thinknimble/tn-forms'
import i18next from '../../i18n/config'
import { PhoneNumberValidator } from 'src/utils/phone'
import { ReservationKindValues } from './options'

const { t } = i18next

export type ReservationFormInputs = {
  checkinDate: IFormField<string | Date>
  checkoutDate: IFormField<string | Date>
  providerConfirmationCode: IFormField<string | null>
  provider: IFormField<string>
  kind: IFormField<ReservationKindValues>
  isPrivate: IFormField<boolean>
  property: IFormField<string>
  notes: IFormField<string | null>
  declaredNumberOfGuests: IFormField<number>
  totalPrice: IFormField<number | string | null>
  totalPriceFloat: IFormField<number | null>
  totalPriceCurrency: IFormField<string>
  guestPaidPrice: IFormField<number | string | null>
  guestPaidPriceFloat: IFormField<number | null>
  guestPaidPriceCurrency: IFormField<string>
}

export class ReservationForm extends Form<ReservationFormInputs> {
  static checkinDate = new FormField({
    label: t('reservationForm.checkinDateLabel'),
    placeholder: t('reservationForm.checkinDatePlaceholder'),
    type: 'datetime-local',
    validators: [],
    value: '',
  })
  static checkoutDate = new FormField({
    label: t('reservationForm.checkoutDateLabel'),
    placeholder: t('reservationForm.checkoutDatePlaceholder'),
    type: 'datetime-local',
    validators: [],
    value: '',
  })
  static providerConfirmationCode = new FormField({
    label: t('reservationForm.providerConfirmationCodeLabel'),
    placeholder: t('reservationForm.providerConfirmationCodePlaceholder'),
    validators: [],
  })
  static provider = new FormField({
    label: t('reservationForm.providerLabel'),
    placeholder: t('reservationForm.providerPlaceholder'),
    type: 'option',
    value: 'manual',
    validators: [],
  })
  static kind = new FormField({
    value: 'reservation',
  })
  static isPrivate = new FormField({
    label: t('reservationForm.privateLabel'),
    value: false,
  })
  static property = new FormField({
    label: t('reservationForm.propertyLabel'),
    placeholder: t('reservationForm.propertyPlaceholder'),
    type: 'text',
    value: '',
    validators: [new RequiredValidator({ message: t('reservationForm.propertyRequired') })],
  })
  static notes = new FormField({
    label: t('reservationForm.notesLabel'),
    placeholder: t('reservationForm.notesPlaceholder'),
    type: 'long-text',
    validators: [],
  })

  static declaredNumberOfGuests = new FormField({
    label: t('reservationForm.numberOfGuestsLabel'),
    placeholder: t('reservationForm.numberOfGuestsPlaceholder'),
    type: 'number',
    value: 1,
  })

  static totalPrice = new FormField({
    name: 'totalPrice',
    label: t('reservationForm.totalPriceLabel'),
    placeholder: t('reservationForm.totalPricePlaceholder'),
    type: 'text',
    value: 0,
  })
  static totalPriceFloat = new FormField({
    name: 'totalPriceFloat',
    label: t('reservationForm.totalPriceLabel'),
    placeholder: t('reservationForm.totalPricePlaceholder'),
    type: 'text',
    value: 0,
  })
  static totalPriceCurrency = new FormField({
    value: 'COP',
  })
  static guestPaidPrice = new FormField({
    name: 'totalPrice',
    label: t('reservationForm.guestPaidPriceLabel'),
    placeholder: t('reservationForm.guestPaidPricePlaceholder'),
    type: 'text',
    value: 0,
  })
  static guestPaidPriceFloat = new FormField({
    name: 'totalPriceFloat',
    label: t('reservationForm.guestPaidPriceLabel'),
    placeholder: t('reservationForm.guestPaidPricePlaceholder'),
    type: 'text',
    value: 0,
  })
  static guestPaidPriceCurrency = new FormField({
    value: 'COP',
  })
  // static dynamicFormValidators = {
  //   checkoutDate: [
  //     new DynamicMinDateValidator({
  //       matcher: 'checkinDate',
  //     }),
  //   ],
  // }
  get formattedValue() {
    const totalPriceAmt = this.field.totalPriceFloat.value
    const guestPaidPriceAmt = this.field.guestPaidPriceFloat.value
    return {
      checkinDate: new Date(this.field.checkinDate.value!).toISOString(),
      checkoutDate: new Date(this.field.checkoutDate.value!).toISOString(),
      providerConfirmationCode: this.field.providerConfirmationCode.value,
      provider: this.field.provider.value,
      kind: this.field.kind.value,
      isPrivate: Boolean(this.field.isPrivate.value),
      property: this.field.property.value,
      notes: this.field.notes.value,
      declaredNumberOfGuests: this.field.declaredNumberOfGuests.value,
      totalPrice: totalPriceAmt,
      totalPriceCurrency: this.field.totalPriceCurrency.value,
      guestPaidPrice: guestPaidPriceAmt,
      guestPaidPriceCurrency: this.field.guestPaidPriceCurrency.value,
    }
  }
}

export type TReservationForm = ReservationForm & ReservationFormInputs

/** tn-forms' EmailValidator only skips null/undefined, so an empty optional field ('')
 * still fails. This one lets a blank value through and validates anything typed. */
export class OptionalEmailValidator extends EmailValidator {
  call(value: unknown) {
    if (value === null || value === undefined || String(value).trim() === '') return
    super.call(value)
  }
}

export type QuickAddGuestFormInputs = {
  firstName: IFormField<string>
  lastName: IFormField<string>
  phoneNumber: IFormField<string>
  email: IFormField<string>
  preferredLanguage: IFormField<string>
}

export class QuickAddGuestForm extends Form<QuickAddGuestFormInputs> {
  static firstName = new FormField({
    label: t('quickAddGuest.firstName'),
    placeholder: t('quickAddGuest.firstName'),
    type: 'text',
    value: '',
    validators: [new RequiredValidator({ message: t('quickAddGuest.required') })],
  })
  static lastName = new FormField({
    label: t('quickAddGuest.lastName'),
    placeholder: t('quickAddGuest.lastName'),
    type: 'text',
    value: '',
    validators: [new RequiredValidator({ message: t('quickAddGuest.required') })],
  })
  static phoneNumber = new FormField({
    label: t('quickAddGuest.phone'),
    placeholder: '+57...',
    type: 'text',
    value: '',
    validators: [
      new PhoneNumberValidator({ message: t('common.invalidPhone'), isRequired: false }),
    ],
  })
  static email = new FormField({
    label: t('quickAddGuest.email'),
    placeholder: t('quickAddGuest.email'),
    type: 'email',
    value: '',
    validators: [
      new OptionalEmailValidator({ message: t('quickAddGuest.invalidEmail'), isRequired: false }),
    ],
  })
  static preferredLanguage = new FormField({
    label: t('quickAddGuest.language'),
    placeholder: '',
    type: 'text',
    value: 'es',
    validators: [],
  })
}

export type TQuickAddGuestForm = QuickAddGuestForm & QuickAddGuestFormInputs
